/**
 * Beauty by Earth — Self Tanner Collection
 * assets/bbe-collection.js
 *
 * Responsibilities:
 *  1.  Desktop quick-add tray — variant chip selection + price update
 *  2.  Keyboard :focus-within — same tray works for keyboard users
 *  3.  Mobile bottom sheet — open/close/populate from /products/{handle}.js
 *  4.  Bottom sheet: variant selection, price update, CTA update
 *  5.  Bottom sheet: selling plan selector
 *  6.  Bottom sheet: focus trap + Escape close + overlay close
 *  7.  Ajax add-to-cart via /cart/add.js
 *  8.  Cart count update via /cart.js
 *  9.  Cart drawer hook (theme-agnostic, multiple fallback patterns)
 * 10.  Filter drawer open/close + active filter chips
 * 11.  Mobile sort bottom sheet
 * 12.  Sort select → URL navigation
 * 13.  Sticky mobile bar header-offset calculation
 * 14.  Error handling on every async path
 *
 * Zero dependencies. Vanilla JS ES2019+. IIFE for clean scope.
 */

(function () {
  'use strict';

  /* ── CONSTANTS ─────────────────────────── */
  const CART_ADD_URL   = '/cart/add.js';
  const CART_STATE_URL = '/cart.js';
  const PRODUCT_URL    = (h) => `/products/${h}.js`;

  /* ── PRODUCT CACHE (avoids repeat fetches) */
  const productCache = {};

  /* ── ELEMENT REFS ───────────────────────── */
  const grid        = () => document.getElementById('bbe-product-grid') || document.getElementById('product-grid');
  const section     = () => document.querySelector('.bbe-collection, .collection-bbe--filter-tabs');
  const sheetEl     = () => document.getElementById('bbe-quick-add-sheet');
  const sheetOvEl   = () => document.getElementById('bbe-sheet-overlay');

  /* ── BOOT ────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    bindGrid();
    bindSheet();
    bindFilter();
    bindSort();
    bindCollectionTabs();
    calcMobileBarOffset();
    injectNotifStyle();
    bindLpChromeCart();
  }


  /* ═══════════════════════════════════════════
     1–2. DESKTOP TRAY — VARIANT CHIPS + KEYBOARD
  ═══════════════════════════════════════════ */

  function bindGrid() {
    const g = grid();
    if (!g) return;

    g.addEventListener('click', (e) => {

      // Desktop variant chip in tray
      const chip = e.target.closest('.bbe-card__var-chip');
      if (chip && !chip.disabled) {
        handleDesktopChip(chip);
        return;
      }

      // "X more" chip → open sheet
      const moreChip = e.target.closest('[data-open-sheet]');
      if (moreChip) {
        e.preventDefault();
        const handle = moreChip.dataset.openSheet;
        const card   = moreChip.closest('.bbe-card');
        openSheet(handle, moreChip, card);
        return;
      }

      // Desktop quick-add button
      const qa = e.target.closest('[data-quick-add]');
      if (qa) {
        e.preventDefault();
        handleDesktopQuickAdd(qa);
        return;
      }

      // Mobile CTA button
      const mobileAdd = e.target.closest('[data-mobile-add]');
      if (mobileAdd) {
        e.preventDefault();
        handleMobileCta(mobileAdd);
      }
    });
  }

  /* Desktop chip selection — updates price, compare, CTA text */
  function handleDesktopChip(chip) {
    const card = chip.closest('.bbe-card');
    if (!card) return;

    // Deselect all chips in this card's tray
    card.querySelectorAll('.bbe-card__var-chip').forEach(c => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-pressed', 'false');
    });
    chip.classList.add('is-selected');
    chip.setAttribute('aria-pressed', 'true');

    const variantId    = chip.dataset.variantId;
    const price        = parseInt(chip.dataset.variantPrice, 10);
    const compare      = parseInt(chip.dataset.variantCompare, 10) || 0;
    const available    = chip.dataset.variantAvailable === 'true';
    const variantTitle = chip.dataset.variantTitle || '';

    // Update card price display
    updateCardPrice(card, price, compare);

    // Update quick-add button
    const qa = card.querySelector('[data-quick-add]');
    if (qa) {
      qa.dataset.variantId = variantId;
      qa.dataset.direct    = 'true';
      qa.dataset.hasVariants = 'false';
      const span = qa.querySelector('[data-cta-text]');
      if (available) {
        setBbeAtcContent(span, 'Add to Bag', price, compare);
      } else if (span) {
        span.textContent = 'Sold Out';
      }
      qa.disabled = !available;
      qa.setAttribute('aria-label', (available ? 'Add to Bag' : 'Sold Out') + ' — ' + variantTitle);
    }
  }

  /* Desktop quick-add */
  function handleDesktopQuickAdd(btn) {
    const isDirect    = btn.dataset.direct === 'true';
    const hasVariants = btn.dataset.hasVariants === 'true';
    const variantId   = btn.dataset.variantId;
    const handle      = btn.dataset.productHandle;

    if (!isDirect && hasVariants) {
      // Variant not yet selected — open sheet
      const card = btn.closest('.bbe-card');
      openSheet(handle, btn, card);
      return;
    }

    if (!variantId) return;
    addToCart({ id: variantId, quantity: 1 }, btn);
  }

  /* Update card price — called after chip selection or sheet variant select */
  function updateCardPrice(card, price, compare) {
    const priceEl   = card.querySelector('.bbe-card__price');
    const compareEl = card.querySelector('.bbe-card__compare');
    const saveEl    = card.querySelector('.bbe-card__save-badge');

    if (priceEl) {
      priceEl.textContent = formatMoney(price);
      priceEl.classList.toggle('bbe-card__price--sale', compare > price);
    }

    if (compare > price) {
      const saved = compare - price;
      const pct   = Math.round(saved / compare * 100);
      if (compareEl) {
        compareEl.innerHTML = `<s>${formatMoney(compare)}</s>`;
        compareEl.hidden = false;
      }
      if (saveEl) { saveEl.textContent = `${pct}% off`; saveEl.hidden = false; }
    } else {
      if (compareEl) compareEl.hidden = true;
      if (saveEl)    saveEl.hidden = true;
    }
  }


  /* ═══════════════════════════════════════════
     3–6. MOBILE BOTTOM SHEET
  ═══════════════════════════════════════════ */

  const sheet = {
    el:          null,
    overlay:     null,
    closeBtn:    null,
    imgEl:       null,
    titleEl:     null,
    reviewsEl:   null,
    priceEl:     null,
    compareEl:   null,
    saveEl:      null,
    variantsEl:  null,
    varLabelEl:  null,
    planBlockEl: null,
    plansEl:     null,
    addBtn:      null,
    detailsLink: null,
    loadingEl:   null,
    product:     null,
    variant:     null,
    planId:      null,
    trigger:     null,   // element that opened the sheet (focus restore)
    focusable:   [],
  };

  function bindSheet() {
    sheet.el         = sheetEl();
    sheet.overlay    = sheetOvEl();
    if (!sheet.el) return;

    sheet.closeBtn   = document.getElementById('bbe-sheet-close');
    sheet.imgEl      = document.getElementById('bbe-sheet-img');
    sheet.titleEl    = document.getElementById('bbe-sheet-title');
    sheet.reviewsEl  = document.getElementById('bbe-sheet-reviews');
    sheet.priceEl    = document.getElementById('bbe-sheet-price');
    sheet.compareEl  = document.getElementById('bbe-sheet-compare');
    sheet.saveEl     = document.getElementById('bbe-sheet-save');
    sheet.variantsEl = document.getElementById('bbe-sheet-variants');
    sheet.varLabelEl = document.getElementById('bbe-sheet-variant-label');
    sheet.planBlockEl= document.getElementById('bbe-sheet-plan-block');
    sheet.plansEl    = document.getElementById('bbe-sheet-plans');
    sheet.addBtn     = document.getElementById('bbe-sheet-add-btn');
    sheet.detailsLink= document.getElementById('bbe-sheet-details-link');
    sheet.loadingEl  = document.getElementById('bbe-sheet-loading');

    sheet.closeBtn?.addEventListener('click', closeSheet);
    sheet.overlay?.addEventListener('click', closeSheet);
    sheet.addBtn?.addEventListener('click', handleSheetAdd);
    sheet.el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeSheet();
      if (e.key === 'Tab')    trapFocus(e);
    });
  }

  /* Mobile CTA handler */
  function handleMobileCta(btn) {
    const hasVariants = btn.dataset.hasVariants === 'true';
    const variantId   = btn.dataset.variantId;
    const handle      = btn.dataset.productHandle;

    if (!hasVariants && variantId) {
      addToCart({ id: variantId, quantity: 1 }, btn);
    } else {
      openSheet(handle, btn, btn.closest('.bbe-card'));
    }
  }

  /* Open sheet */
  async function openSheet(handle, triggerEl, card) {
    if (!sheet.el || !handle) return;

    sheet.trigger = triggerEl;

    showSheetLoading(true);
    revealSheetDom();

    try {
      const product = await fetchProduct(handle);
      sheet.product = product;
      populateSheet(product);
    } catch (err) {
      console.error('[BBE] Sheet product load failed:', err);
      closeSheet();
    } finally {
      showSheetLoading(false);
    }
  }

  function revealSheetDom() {
    sheet.el.classList.add('is-open');
    sheet.el.setAttribute('aria-hidden', 'false');
    sheet.overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      updateFocusable();
      (sheet.focusable[0] || sheet.el).focus();
    }, 360);
  }

  function closeSheet() {
    if (!sheet.el) return;
    sheet.el.classList.remove('is-open');
    sheet.el.setAttribute('aria-hidden', 'true');
    sheet.overlay?.classList.remove('is-active');
    document.body.style.overflow = '';

    // Restore focus to trigger
    if (sheet.trigger && document.body.contains(sheet.trigger)) {
      sheet.trigger.focus();
    }
    sheet.product = null;
    sheet.variant = null;
    sheet.planId  = null;
    sheet.trigger = null;
  }

  /* Populate sheet from product data */
  function populateSheet(product) {
    // Image
    const img = product.featured_image || product.images?.[0];
    if (sheet.imgEl && img) {
      sheet.imgEl.src = img.startsWith('//') ? 'https:' + img : img;
      sheet.imgEl.alt = product.title;
    }

    // Title
    if (sheet.titleEl) sheet.titleEl.textContent = product.title;

    // Details link
    if (sheet.detailsLink) sheet.detailsLink.href = `/products/${product.handle}`;

    // Reviews (metafields)
    if (sheet.reviewsEl) {
      const rating = product.metafields?.reviews?.rating;
      const count  = product.metafields?.reviews?.rating_count;
      sheet.reviewsEl.innerHTML = (rating && count)
        ? `<span class="bbe-stars">${starsHtml(Math.round(rating))}</span>
           <span class="bbe-card__rating-num">${parseFloat(rating).toFixed(1)}</span>
           <span class="bbe-card__rating-ct">(${count})</span>`
        : '';
    }

    // Selling plans
    const hasPlans = product.selling_plan_groups?.length > 0;
    if (sheet.planBlockEl) sheet.planBlockEl.hidden = !hasPlans;
    if (hasPlans) populatePlans(product.selling_plan_groups);

    // Variant label
    const optionName = product.options?.[0] || 'Option';
    if (sheet.varLabelEl) sheet.varLabelEl.textContent = optionName;

    // Variants
    populateSheetVariants(product.variants, optionName);

    // Select first available
    const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
    if (firstAvailable) selectSheetVariant(firstAvailable);

    // Refresh focusable list after populating
    updateFocusable();
  }

  /* Render variant buttons in sheet */
  function populateSheetVariants(variants, optionName) {
    if (!sheet.variantsEl || !variants?.length) return;

    sheet.variantsEl.innerHTML = variants.map(v => `
      <button
        class="bbe-sheet__var-btn${v.available ? '' : ' is-unavailable'}"
        type="button"
        data-variant-id="${v.id}"
        data-variant-price="${v.price}"
        data-variant-compare="${v.compare_at_price || 0}"
        data-variant-available="${v.available}"
        data-variant-title="${escHtml(v.title)}"
        ${v.available ? '' : 'disabled aria-disabled="true"'}
        aria-pressed="false"
        aria-label="${escHtml(v.title)}${v.available ? '' : ' — Sold out'}"
      >${escHtml(v.title)}</button>
    `).join('');

    sheet.variantsEl.querySelectorAll('.bbe-sheet__var-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const v = {
          id:              parseInt(btn.dataset.variantId, 10),
          price:           parseInt(btn.dataset.variantPrice, 10),
          compare_at_price: parseInt(btn.dataset.variantCompare, 10) || 0,
          available:       btn.dataset.variantAvailable === 'true',
          title:           btn.dataset.variantTitle,
        };
        selectSheetVariant(v);
      });
    });
  }

  /* Select a variant in the sheet — updates UI + stored state */
  function selectSheetVariant(variant) {
    sheet.variant = variant;

    // Update chip selection state
    sheet.variantsEl?.querySelectorAll('.bbe-sheet__var-btn').forEach(btn => {
      const sel = parseInt(btn.dataset.variantId, 10) === variant.id;
      btn.classList.toggle('is-selected', sel);
      btn.setAttribute('aria-pressed', sel ? 'true' : 'false');
    });

    // Price
    if (sheet.priceEl) sheet.priceEl.textContent = formatMoney(variant.price);

    const hasCompare = variant.compare_at_price > variant.price;
    if (sheet.compareEl) {
      sheet.compareEl.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
      sheet.compareEl.hidden = !hasCompare;
    }
    if (sheet.saveEl) {
      if (hasCompare) {
        const saved = variant.compare_at_price - variant.price;
        const pct   = Math.round(saved / variant.compare_at_price * 100);
        sheet.saveEl.textContent = `Save ${pct}%`;
        sheet.saveEl.hidden = false;
      } else {
        sheet.saveEl.hidden = true;
      }
    }

    // CTA
    if (sheet.addBtn) {
      if (variant.available) {
        setBbeAtcContent(sheet.addBtn, 'Add to Bag', variant.price, variant.compare_at_price);
      } else {
        sheet.addBtn.textContent = 'Sold Out';
      }
      sheet.addBtn.disabled = !variant.available;
    }
  }

  /* Selling plan buttons */
  function populatePlans(groups) {
    if (!sheet.plansEl) return;
    const plans = groups.flatMap(g => g.selling_plans);
    sheet.selectedPlanId = null;

    sheet.plansEl.innerHTML = `
      <button class="bbe-sheet__plan-btn is-selected" type="button" data-plan-id="" aria-pressed="true">
        <span class="bbe-sheet__plan-label">One-time purchase</span>
        <span class="bbe-sheet__plan-price"></span>
      </button>
      ${plans.map(p => {
        const adj = p.price_adjustments?.[0];
        const pct = adj?.value_type === 'percentage' ? adj.value : 0;
        return `
          <button class="bbe-sheet__plan-btn" type="button" data-plan-id="${p.id}" aria-pressed="false">
            <span class="bbe-sheet__plan-label">
              ${escHtml(p.name)}
              ${pct ? `<span class="bbe-sheet__plan-badge">Save ${pct}%</span>` : ''}
            </span>
            <span class="bbe-sheet__plan-price"></span>
          </button>`;
      }).join('')}
    `;

    sheet.plansEl.querySelectorAll('.bbe-sheet__plan-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        sheet.plansEl.querySelectorAll('.bbe-sheet__plan-btn').forEach(b => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        sheet.planId = btn.dataset.planId || null;
      });
    });
  }

  /* Sheet add-to-cart */
  function handleSheetAdd() {
    if (!sheet.variant?.available) return;
    const payload = { id: sheet.variant.id, quantity: 1 };
    if (sheet.planId) payload.selling_plan = parseInt(sheet.planId, 10);
    addToCart(payload, sheet.addBtn, closeSheet);
  }

  /* Loading overlay */
  function showSheetLoading(show) {
    if (!sheet.loadingEl) return;
    sheet.loadingEl.classList.toggle('is-active', show);
    sheet.loadingEl.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  /* Focus trap */
  function updateFocusable() {
    if (!sheet.el) return;
    sheet.focusable = Array.from(sheet.el.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    ));
  }

  function trapFocus(e) {
    const els   = sheet.focusable;
    if (!els.length) return;
    const first = els[0];
    const last  = els[els.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  }


  /* ═══════════════════════════════════════════
     7–9. AJAX ADD-TO-CART
  ═══════════════════════════════════════════ */

  async function addToCart(payload, triggerBtn, onSuccess) {
    if (!payload?.id) { console.warn('[BBE] addToCart: no variant id'); return; }

    const origText = triggerBtn?.textContent?.trim();
    setButtonLoading(triggerBtn, true, 'Adding…');

    // Preferred path: liquidAjaxCart owns cart state and (via assets/ajax-cart.js
    // on request-end) the cart-drawer open + header count. We must NOT call
    // updateCartCount()/openCartDrawer() here or we double-open the drawer and
    // desync LAC's internal cart. (F-06 / F-07c)
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.add === 'function') {
      window.liquidAjaxCart.add(
        { items: [payload] },
        {
          lastCallback: (requestState) => {
            const ok = requestState?.responseData?.ok !== false;
            if (ok) {
              const item = requestState?.responseData?.body?.items?.[0] || null;
              setButtonSuccess(triggerBtn, '✓ Added', origText);
              if (typeof onSuccess === 'function') onSuccess(item);
            } else {
              const desc = requestState?.responseData?.body?.description;
              setButtonError(triggerBtn, desc || 'Could not add to cart', origText);
            }
          },
        },
      );
      return;
    }

    // Fallback: liquidAjaxCart unavailable — raw fetch with manual count + drawer.
    try {
      const res = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [payload] }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `HTTP ${res.status}`);
      }

      const data = await res.json();
      const item = data.items?.[0];
      if (!item) throw new Error('No item in cart response');

      setButtonSuccess(triggerBtn, '✓ Added', origText);
      updateCartCount();
      openCartDrawer(item);
      if (typeof onSuccess === 'function') onSuccess(item);

    } catch (err) {
      console.error('[BBE] Add to cart failed:', err);
      setButtonError(triggerBtn, err.message, origText);
    }
  }

  /* Update cart count in header via /cart.js */
  function updateCartCount() {
    fetch(CART_STATE_URL, { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        // Try common theme cart count selectors
        [
          '[data-cart-count]',
          '[data-cart-item-count]',
          '.cart-count',
          '.cart__count',
          '#cart-count',
          '.header__cart-count',
          '.site-header__cart-count',
        ].forEach(sel => {
          document.querySelectorAll(sel).forEach(el => {
            el.textContent = cart.item_count;
            el.setAttribute('aria-label', `${cart.item_count} items in cart`);
          });
        });

        // Emit for theme to listen to
        document.dispatchEvent(new CustomEvent('bbe:cart:updated', {
          bubbles: true,
          detail: { cart },
        }));
      })
      .catch(() => {});
  }

  /* Open cart drawer — tries multiple theme patterns */
  function openCartDrawer(addedItem) {
    const attempts = [
      // Dawn / OS2.0
      () => document.querySelector('cart-notification')?.open?.(),
      () => document.querySelector('cart-drawer')?.open?.(),
      // Broadcast event (many themes listen)
      () => document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true, detail: { item: addedItem } })),
      () => document.dispatchEvent(new CustomEvent('cartOpen', { bubbles: true })),
      // Direct element reveal
      () => { const el = document.getElementById('CartDrawer') || document.querySelector('[data-cart-drawer]'); if (el) { el.classList.add('is-active', 'active', 'open'); el.removeAttribute('hidden'); } },
      // Shopify.onCartUpdate (older themes)
      () => window.Shopify?.onCartUpdate?.(),
      // theme.drawer_open (Turbo etc.)
      () => window.theme?.openCartDrawer?.(),
    ];

    for (const fn of attempts) {
      try {
        const result = fn();
        if (result !== undefined) return; // something fired
      } catch (_) {}
    }

    // Last resort: inline toast notification
    if (addedItem && addedItem.title) {
      showToast(`${addedItem.title} added to cart`);
    }
  }

  /* Inline toast (fallback when no cart drawer detected) */
  function bindLpChromeCart() {
    const openBtn = document.querySelector('[data-bbe-coll-open-cart]');
    if (openBtn) {
      openBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openCartDrawer();
      });
    }

    function syncLpCartBadge() {
      let n = 0;
      try {
        if (window.liquidAjaxCart && window.liquidAjaxCart.cart && typeof window.liquidAjaxCart.cart.item_count === 'number') {
          n = window.liquidAjaxCart.cart.item_count;
        }
      } catch (err) {}
      if (n === 0) {
        fetch(CART_STATE_URL, { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (cart && typeof cart.item_count === 'number') updateLpCartBadge(cart.item_count);
          })
          .catch(function () {});
        return;
      }
      updateLpCartBadge(n);
    }

    function updateLpCartBadge(n) {
      const countEl = document.querySelector('[data-bbe-coll-cart-count]');
      const btn = document.querySelector('[data-bbe-coll-open-cart]');
      if (countEl) {
        countEl.textContent = String(n);
        countEl.classList.toggle('bbe-coll__header-cart-count--empty', n === 0);
      }
      if (btn) {
        btn.setAttribute('aria-label', n === 0 ? 'Open shopping cart' : 'Open shopping cart, ' + n + ' items');
      }
    }

    document.addEventListener('liquid-ajax-cart:request-end', syncLpCartBadge);
    document.addEventListener('liquid-ajax-cart:init', syncLpCartBadge);
    document.addEventListener('bbe:cart:updated', syncLpCartBadge);
    syncLpCartBadge();
  }

  function showToast(msg) {
    const existing = document.getElementById('bbe-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'bbe-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
      ${escHtml(msg)}
    `;
    document.body.appendChild(toast);

    // Force reflow then animate in
    void toast.offsetHeight;
    toast.classList.add('bbe-toast--visible');
    setTimeout(() => { toast.classList.remove('bbe-toast--visible'); setTimeout(() => toast.remove(), 350); }, 2800);
  }

  /* Button state helpers */
  function setButtonLoading(btn, loading, text) {
    if (!btn) return;
    if (loading) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.dataset.origText = btn.textContent.trim();
      if (text) btn.textContent = text;
    } else {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  function setButtonSuccess(btn, text, restoreText) {
    if (!btn) return;
    btn.classList.remove('is-loading');
    btn.classList.add('is-added');
    btn.disabled = false;
    btn.textContent = text;
    setTimeout(() => {
      btn.classList.remove('is-added');
      btn.textContent = restoreText || (btn.dataset.origText || 'Add to Bag');
    }, 2000);
  }

  function setButtonError(btn, msg, restoreText) {
    if (!btn) return;
    btn.classList.remove('is-loading');
    btn.disabled = false;
    btn.textContent = msg?.slice(0, 24) || 'Error — try again';
    setTimeout(() => { btn.textContent = restoreText || (btn.dataset.origText || 'Add to Bag'); }, 3000);
  }


  /* ═══════════════════════════════════════════
     10. FILTER DRAWER
  ═══════════════════════════════════════════ */

  function bindFilter() {
    const toggle   = document.getElementById('bbe-filter-toggle');
    const mToggle  = document.getElementById('bbe-m-filter');
    const drawer   = document.getElementById('bbe-filter-drawer');
    const overlay  = document.getElementById('bbe-filter-overlay');
    const closeBtn = document.getElementById('bbe-filter-close');
    const applyBtn = document.getElementById('bbe-filter-apply');

    if (!drawer) return;

    const openFilter = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      toggle?.setAttribute('aria-expanded', 'true');
      setTimeout(() => drawer.focus(), 50);
    };

    const closeFilter = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay?.classList.remove('is-active');
      document.body.style.overflow = '';
      toggle?.setAttribute('aria-expanded', 'false');
      (toggle || mToggle)?.focus();
    };

    toggle?.addEventListener('click', openFilter);
    mToggle?.addEventListener('click', openFilter);
    closeBtn?.addEventListener('click', closeFilter);
    overlay?.addEventListener('click', closeFilter);
    drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeFilter(); });

    // Apply: build URL from checked inputs and navigate
    applyBtn?.addEventListener('click', () => {
      const params = new URLSearchParams(window.location.search);
      // Remove existing filter params
      for (const key of [...params.keys()]) {
        if (key.startsWith('filter.') || key === 'page') params.delete(key);
      }
      // Add active filter inputs
      drawer.querySelectorAll('.bbe-filter-input:checked').forEach(input => {
        params.append(input.name, input.value);
      });
      // Price range
      const minEl = drawer.querySelector('#filter-price-min');
      const maxEl = drawer.querySelector('#filter-price-max');
      if (minEl?.value) params.set(minEl.name, minEl.value);
      if (maxEl?.value) params.set(maxEl.name, maxEl.value);

      window.location.search = params.toString();
    });

    // Live count of active filters
    const updateFilterCount = () => {
      const count = drawer.querySelectorAll('.bbe-filter-input:checked').length;
      const countEls = document.querySelectorAll('#bbe-filter-count, #bbe-m-filter-count');
      countEls.forEach(el => {
        el.textContent = count || '';
        el.hidden = count === 0;
      });
    };

    drawer.querySelectorAll('.bbe-filter-input').forEach(input => {
      input.addEventListener('change', updateFilterCount);
    });
    updateFilterCount();

    // Build active filter chips from URL on load
    buildActiveFilterChips();
  }

  function buildActiveFilterChips() {
    const container = document.getElementById('bbe-active-filters');
    if (!container) return;

    const params = new URLSearchParams(window.location.search);
    const chips  = [];

    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      const chip = document.createElement('button');
      chip.className = 'bbe-active-filter-chip';
      chip.type = 'button';
      chip.textContent = value;
      chip.setAttribute('aria-label', `Remove filter: ${value}`);
      chip.innerHTML += ' ✕';
      chip.addEventListener('click', () => {
        const p = new URLSearchParams(window.location.search);
        // Remove only this specific param+value pair
        const existing = p.getAll(key).filter(v => v !== value);
        p.delete(key);
        existing.forEach(v => p.append(key, v));
        window.location.search = p.toString();
      });
      chips.push(chip);
    });

    if (chips.length) {
      container.hidden = false;
      chips.forEach(c => container.appendChild(c));
    }
  }


  /* ═══════════════════════════════════════════
     11–12. SORT — DESKTOP SELECT + MOBILE SHEET
  ═══════════════════════════════════════════ */

  function bindSort() {
    // Desktop sort select
    const select = document.getElementById('bbe-sort-by');
    if (select) {
      select.addEventListener('change', () => navigateSortBy(select.value));
    }

    // Mobile sort sheet
    const mSortBtn = document.getElementById('bbe-m-sort');
    const sortSheet  = document.getElementById('bbe-sort-sheet');
    const sortOv     = document.getElementById('bbe-sort-overlay');
    const sortClose  = document.getElementById('bbe-sort-close');

    if (!sortSheet) return;

    const openSort = () => {
      sortSheet.classList.add('is-open');
      sortSheet.setAttribute('aria-hidden', 'false');
      sortOv?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      setTimeout(() => sortSheet.focus(), 50);
    };

    const closeSort = () => {
      sortSheet.classList.remove('is-open');
      sortSheet.setAttribute('aria-hidden', 'true');
      sortOv?.classList.remove('is-active');
      document.body.style.overflow = '';
      mSortBtn?.focus();
    };

    mSortBtn?.addEventListener('click', openSort);
    sortClose?.addEventListener('click', closeSort);
    sortOv?.addEventListener('click', closeSort);
    sortSheet.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSort(); });

    // Sort option buttons
    document.querySelectorAll('.bbe-sort-sheet__option').forEach(btn => {
      btn.addEventListener('click', () => {
        navigateSortBy(btn.dataset.sortValue);
      });
    });
  }

  function navigateSortBy(value) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page'); // reset to page 1
    window.location.href = url.toString();
  }


  /* ═══════════════════════════════════════════
     13. STICKY MOBILE BAR — HEADER OFFSET
  ═══════════════════════════════════════════ */

  function calcMobileBarOffset() {
    const setOffset = () => {
      let offset = 0;
      const lpNav = document.querySelector('.bbe-collection--lp-chrome .bbe-coll__nav-shell');
      if (lpNav) {
        offset = lpNav.getBoundingClientRect().height;
      } else {
        const announcement = document.querySelector(
          '#shopify-section-announcement-bar, .shopify-section-announcement-bar, .announcement-bar-section'
        );
        const siteHeader = document.querySelector(
          '#shopify-section-new-header, .new-header, header.header, #shopify-section-header, .site-header'
        );
        if (announcement) {
          offset += announcement.getBoundingClientRect().height;
        }
        if (siteHeader) {
          offset += siteHeader.getBoundingClientRect().height;
        }
      }
      document.documentElement.style.setProperty('--bbe-header-offset', offset + 'px');
    };
    setOffset();
    window.addEventListener('resize', debounce(setOffset, 120));
  }

  function bindCollectionTabs() {
    const root = section();
    if (!root) return;

    const toolbars = root.querySelectorAll('[data-bbe-coll-filter-toolbar]');
    if (!toolbars.length) return;

    const filterBtns = root.querySelectorAll(
      '[data-bbe-coll-filter-toolbar] .bbe-coll__filter[data-bbe-coll-tab-id]'
    );
    if (!filterBtns.length) return;

    const countEl = root.querySelector('.bbe-coll__product-count');

    function cardTabTokens(card) {
      const s = (card.getAttribute('data-bbe-coll-filters') || card.getAttribute('data-bogl-filters') || '').trim();
      if (!s) return [];
      return s.split(/\s+/).filter(Boolean);
    }

    function cardMatchesTab(card, tabId) {
      return cardTabTokens(card).indexOf(tabId) !== -1;
    }

    function getFilterItem(card) {
      if (card.matches('li.collection-bbe-card')) return card;
      return card.closest('.bbe-coll__item') || card.closest('li.collection-bbe-card') || card;
    }

    function countVisibleProducts() {
      const bbeCards = root.querySelectorAll('li.collection-bbe-card[data-bbe-coll-filters]:not([hidden])');
      if (bbeCards.length) return bbeCards.length;
      return root.querySelectorAll('.bbe-card:not([hidden])').length;
    }

    function setFilteredHidden(el, hidden) {
      if (hidden) {
        el.setAttribute('hidden', '');
        if ('inert' in el) el.inert = true;
      } else {
        el.removeAttribute('hidden');
        if ('inert' in el) el.inert = false;
      }
    }

    function updateCount(visible) {
      if (!countEl) return;
      const n = visible != null ? visible : countVisibleProducts();
      countEl.textContent = n === 1 ? '1 product' : n + ' products';
    }

    function applyTabFilter(tabId, fType) {
      const cards = root.querySelectorAll('li.collection-bbe-card[data-bbe-coll-filters], .bbe-card[data-bbe-coll-filters]');
      const promo = root.querySelector('[data-bbe-coll-promo]');
      const adTiles = root.querySelectorAll('[data-bogl-video-grid]');
      let visible = 0;

      if (fType === 'all') {
        cards.forEach((c) => {
          setFilteredHidden(getFilterItem(c), false);
          visible += 1;
        });
        if (promo) setFilteredHidden(promo, false);
        adTiles.forEach((tile) => setFilteredHidden(tile, false));
      } else {
        cards.forEach((c) => {
          const item = getFilterItem(c);
          const show = cardMatchesTab(c, tabId);
          setFilteredHidden(item, !show);
          if (show) visible += 1;
        });
        if (promo) setFilteredHidden(promo, true);
        adTiles.forEach((tile) => setFilteredHidden(tile, true));
      }
      updateCount(visible);
    }

    function syncToolbar(btn) {
      const tabId = btn.getAttribute('data-bbe-coll-tab-id') || '';
      const fType = btn.getAttribute('data-bbe-coll-filter-type') || 'all';

      filterBtns.forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      toolbars.forEach((toolbar) => {
        toolbar.querySelectorAll('.bbe-coll__filter[data-bbe-coll-tab-id]').forEach((b) => {
          const match = b.getAttribute('data-bbe-coll-tab-id') === tabId;
          b.classList.toggle('is-active', match);
          b.setAttribute('aria-pressed', match ? 'true' : 'false');
        });
      });

      applyTabFilter(tabId, fType);
    }

    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => syncToolbar(btn));
    });

    const initialBtn =
      root.querySelector('[data-bbe-coll-filter-toolbar] .bbe-coll__filter.is-active[data-bbe-coll-tab-id]') ||
      filterBtns[0];

    if (initialBtn) syncToolbar(initialBtn);
  }


  /* ═══════════════════════════════════════════
     HELPERS
  ═══════════════════════════════════════════ */

  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    const res = await fetch(PRODUCT_URL(handle), { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`Product fetch failed: ${handle} (${res.status})`);
    const data = await res.json();
    productCache[handle] = data;
    return data;
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    const amount = (cents / 100).toFixed(2);
    // Basic dollar formatting — extend with Shopify money_format if needed
    return '$' + (parseFloat(amount) % 1 === 0 ? Math.floor(parseFloat(amount)).toString() : amount);
  }

  function shouldShowCompareAt(compare, price) {
    return compare > 0 && compare > price;
  }

  function buildBbeAtcHtml(label, price, compare) {
    const showCompare = shouldShowCompareAt(compare, price);
    return [
      `<span class="bbe-card__atc-label">${label}</span>`,
      `<span class="bbe-card__atc-sep" aria-hidden="true"> • </span>`,
      `<span class="bbe-card__atc-prices" data-atc-prices>`,
      showCompare ? `<s class="bbe-card__atc-compare" data-atc-compare>${formatMoney(compare)}</s>` : '',
      `<span class="bbe-card__atc-price" data-atc-price>${formatMoney(price)}</span>`,
      `</span>`,
    ].join('');
  }

  function setBbeAtcContent(container, label, price, compare) {
    if (!container) return;
    container.innerHTML = buildBbeAtcHtml(label, price, compare);
  }

  function starsHtml(n) {
    let s = '';
    for (let i = 1; i <= 5; i++) s += i <= n ? '★' : '☆';
    return s;
  }

  function escHtml(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  function debounce(fn, delay) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
  }

  /* Inject toast + notification CSS once */
  function injectNotifStyle() {
    if (document.getElementById('bbe-toast-style')) return;
    const style = document.createElement('style');
    style.id = 'bbe-toast-style';
    style.textContent = `
      #bbe-toast {
        position: fixed;
        bottom: calc(var(--bbe-sticky-atc-height, 72px) + 12px);
        left: 50%;
        transform: translateX(-50%) translateY(16px);
        background: #000;
        color: #fff;
        padding: 11px 18px;
        border-radius: 8px;
        font-family: var(--bbe-bod, sans-serif);
        font-size: 13px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 9px;
        z-index: 9999;
        box-shadow: 0 4px 20px rgba(0,0,0,.2);
        opacity: 0;
        transition: opacity .25s, transform .25s;
        white-space: nowrap;
        pointer-events: none;
      }
      #bbe-toast.bbe-toast--visible {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    `;
    document.head.appendChild(style);
  }

})();
